import { classNames } from "@/utility/jsx";
import { type ReactNode, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import styles from "./QuickAccessLabel.module.scss";

const LOADING_DASH_LENGTH = 18;
const LOADING_STROKE_WIDTH = 3;
const LOADING_RADIUS = 4;

type LoadingLabelFrameProps = {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  isLoading?: boolean;
  loadingTestId?: string;
};

export const LoadingLabelFrame = forwardRef<HTMLDivElement, LoadingLabelFrameProps>(function LoadingLabelFrame(
  { children, className, dataTestId, isLoading = false, loadingTestId },
  forwardedRef,
) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [labelSize, setLabelSize] = useState({ width: 0, height: 0 });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      frameRef.current = node;

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
        return;
      }

      if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  useEffect(() => {
    const element = frameRef.current;
    if (!element) {
      return;
    }

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setLabelSize((current) => {
        const nextWidth = Math.round(rect.width);
        const nextHeight = Math.round(rect.height);

        if (current.width === nextWidth && current.height === nextHeight) {
          return current;
        }

        return { width: nextWidth, height: nextHeight };
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateSize();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const measuredWidth = Math.max(labelSize.width, 1);
  const measuredHeight = Math.max(labelSize.height, 1);
  const spinnerInset = LOADING_STROKE_WIDTH / 2;
  const spinnerWidth = Math.max(measuredWidth - LOADING_STROKE_WIDTH, 0);
  const spinnerHeight = Math.max(measuredHeight - LOADING_STROKE_WIDTH, 0);

  return (
    <div ref={setRefs} data-testid={dataTestId} className={classNames(className, styles["label-shell"])}>
      <svg
        className={classNames(styles["label-loading-svg"], isLoading ? styles["label-loading-svg--visible"] : "")}
        width={measuredWidth}
        height={measuredHeight}
        viewBox={`0 0 ${measuredWidth} ${measuredHeight}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect
          className={styles["label-loading-svg-arc"]}
          x={spinnerInset}
          y={spinnerInset}
          width={spinnerWidth}
          height={spinnerHeight}
          rx={LOADING_RADIUS}
          ry={LOADING_RADIUS}
          pathLength={100}
          strokeDasharray={`${LOADING_DASH_LENGTH} ${100 - LOADING_DASH_LENGTH}`}
        />
      </svg>
      <div className={styles["label-background"]} data-testid={isLoading ? loadingTestId : undefined}>
        <div className={styles["label-content"]}>{children}</div>
      </div>
    </div>
  );
});
